import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import {
  searchQuotesSortingToPropertySortings,
  searchQuotesSortingToQuoteTemplatesSorting,
} from "../../templates/helpers.js";
import {
  TemplateBase,
  TemplateListItem,
  TemplatesCollectionView,
  TemplatesDisplay,
} from "../../templates/quote/TemplatesDisplay.js";
import { TemplatesQuoteOverviewModal } from "../../templates/quote/TemplatesQuoteOverviewModal.js";
import { useTemplatesQueryVariablesInState } from "../../templates/quote/useTemplatesQueryVariables.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { TemplatePlaceholderFillButton } from "../buttons/TemplatePlaceholderFillButton.js";
import { useSetItemTemplatePlaceholderTemplateMutation } from "../buttons/TemplatePlaceholderSelectTemplateButton.generated.js";
import { isPlaceholderEmpty } from "../helpers.js";
import type {
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
} from "../hooks/useCreateItem.js";
import {
  filterDefinitionsToFormValues,
  sortingDefinitionsToFormValues,
} from "../templatePlaceholderSearchDefaults.js";
import {
  PLACEHOLDER_SELECTION_PAGE_SIZE,
  PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS,
} from "./constants.js";
import type { PlaceholderPick } from "./placeholderPick.js";
import {
  CURRENT_SELECTION_ROW_SX,
  CurrentSelectionChip,
} from "./CurrentSelectionChip.js";
import { HandBuiltContentRow } from "./HandBuiltContentRow.js";
import {
  TemplatePlaceholderSelectionPanel_HandBuiltContentQuery,
  TemplatePlaceholderSelectionPanel_ItemFragment,
  useTemplatePlaceholderSelectionPanel_HandBuiltContentQuery,
  useTemplatePlaceholderSelectionPanel_OriginTemplateQuery,
} from "./TemplatePlaceholderSelectionPanel.generated.js";

const ALLOWED_VIEWS: TemplatesCollectionView[] = ["list", "gallery", "table"];

const getHandBuiltItems = (
  data: TemplatePlaceholderSelectionPanel_HandBuiltContentQuery | undefined
) => (data?.item.__typename === "Item" ? data.item.children : null);

/** Lets the panel fill an empty placeholder through the fill picker. */
export interface PlaceholderItemCreation {
  createItemsWithProducts: (
    args: CreateItemsWithProductsArgs
  ) => Promise<unknown>;
  createItemsFromTemplates: (
    args: CreateItemsFromTemplatesArgs
  ) => Promise<unknown>;
}

interface Props {
  projectId: string | null;
  docId: string;
  item: TemplatePlaceholderSelectionPanel_ItemFragment;
  refetchWizardQuestions: () => Promise<unknown>;
  handleClose: () => void;
  /**
   * The placeholder was filled. Distinct from `handleClose`, which the search
   * surface also calls on a plain dismiss: a surface that steps on an answer
   * needs the two apart.
   */
  onApplied?: (pick: PlaceholderPick) => Promise<void>;
  /** Offers the fill action for an empty placeholder; the surface owns the permission check. */
  itemCreation?: PlaceholderItemCreation;
}

export const TemplatePlaceholderSelectionPanel = ({
  projectId,
  docId,
  item,
  refetchWizardQuestions,
  handleClose,
  onApplied,
  itemCreation,
}: Props) => {
  const { t } = useTranslate(["TemplatesSearch", "QuoteItem", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [setItemTemplatePlaceholderTemplate] =
    useSetItemTemplatePlaceholderTemplateMutation({ client });

  const initialFilters = React.useMemo(
    () => filterDefinitionsToFormValues(item.templateSearchFilterDefaults),
    [item.templateSearchFilterDefaults]
  );
  const defaultSorting = React.useMemo(
    () => sortingDefinitionsToFormValues(item.templateSearchSortingDefaults),
    [item.templateSearchSortingDefaults]
  );
  const initialSorting = React.useMemo(
    () => searchQuotesSortingToQuoteTemplatesSorting(defaultSorting),
    [defaultSorting]
  );
  // Property sortings have no grid column, so the column conversion above drops
  // them; they are appended to the query after the column sort instead.
  const seedPropertySortings = React.useMemo(
    () => searchQuotesSortingToPropertySortings(defaultSorting),
    [defaultSorting]
  );
  const fixedFilters = React.useMemo(
    () => ({
      applicableFor: ["QUOTE" as const],
      templateTypeIds: item.templateSearchFilterDefaults.templateTypeIdFilter
        ? [item.templateSearchFilterDefaults.templateTypeIdFilter.valueId]
        : null,
    }),
    [item.templateSearchFilterDefaults.templateTypeIdFilter]
  );

  const {
    queryBaseVariables,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    paginationModel,
    setPaginationModel,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInState({
    initialFilters,
    initialSorting,
    initialSearchTerm: "",
    perPageDefaultAmount: PLACEHOLDER_SELECTION_PAGE_SIZE,
  });

  // An unfilled placeholder can still carry the origin of the template it was
  // cloned from, which is not a choice made for this slot.
  const filledFromId = item.isTemplatePlaceholderFilled
    ? (item.templateOriginQuoteId ?? null)
    : null;

  // Marks the template this placeholder was filled from, when it is among the
  // results. Only the id is read by `TemplatesDisplay`.
  const selectedTemplate = React.useMemo(
    () => (filledFromId ? { id: filledFromId } : null),
    [filledFromId]
  );

  const originQuery = useTemplatePlaceholderSelectionPanel_OriginTemplateQuery({
    client,
    variables: { id: filledFromId ?? "" },
    skip: !filledFromId,
    // An unreadable origin just pins nothing; the panel opens on its own.
    context: { noErrorNotification: true },
  });
  const originTemplate = getDataOrNull(
    originQuery.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate;
  const [inspectingOrigin, setInspectingOrigin] = React.useState(false);

  const isHandBuilt = !item.isTemplatePlaceholderFilled && item.hasChildren;
  const handBuiltQuery =
    useTemplatePlaceholderSelectionPanel_HandBuiltContentQuery({
      client,
      variables: { projectId, docId, itemId: item.id },
      skip: !isHandBuilt,
      fetchPolicy: "cache-and-network",
    });
  const handBuiltItems = getHandBuiltItems(handBuiltQuery.data);
  const placeholderTemplateType = item.placeholderForTemplateType?.templateType;
  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);

  // Ignores repeat picks until the fill, the wizard refetch and the step have settled.
  const applying = React.useRef(false);
  const handleTemplateSelect = async (template: TemplateBase) => {
    if (!template.resolvedAsReadModelVersionNumber || applying.current) return;
    applying.current = true;
    try {
      if (isHandBuilt) {
        const confirmProcess = confirmProcessRef.current;
        if (!confirmProcess) throw new Error("ConfirmProcess is not mounted");
        // The cached summary may predate edits, so the count shown is read fresh.
        const replacedItems = getHandBuiltItems(
          (await handBuiltQuery.refetch()).data
        );
        if (!replacedItems) {
          enqueueSnackbar(
            t("Something went wrong. Please try again.", { ns: "Global" }),
            { variant: "error" }
          );
          return;
        }
        const confirmed = await confirmProcess.startConfirmProcess({
          title: t("Replace the placeholder's content?", { ns: "QuoteItem" }),
          text: t(
            'This placeholder holds {count, plural, one {# item} other {# items}} added by hand. Selecting "{templateTitle}" replaces this content with the template.',
            {
              ns: "QuoteItem",
              count: replacedItems.length,
              templateTitle: template.title,
            }
          ),
          confirmButtonLabel: t("Replace content", { ns: "QuoteItem" }),
        });
        if (!confirmed) return;
      }
      await setItemTemplatePlaceholderTemplate({
        variables: {
          input: {
            docId,
            itemId: item.id,
            projectId,
            templateQuoteId: template.id,
            templateQuoteIdAtVersionNumber:
              template.resolvedAsReadModelVersionNumber,
          },
        },
      });
      await refetchWizardQuestions();
      await onApplied?.({
        kind: "template",
        templateId: template.id,
        title: template.title,
      });
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Cycle in templates detected"))
          enqueueSnackbar(
            t("Creating cycle linking in templates is not allowed", {
              ns: "TemplatesSearch",
            }),
            { variant: "error" }
          );
        else enqueueSnackbar(error.message, { variant: "error" });
      }
    } finally {
      applying.current = false;
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <ConfirmProcess ref={confirmProcessRef} />
      {isHandBuilt && handBuiltItems && (
        <HandBuiltContentRow items={handBuiltItems} />
      )}
      {itemCreation && placeholderTemplateType && isPlaceholderEmpty(item) && (
        <Stack direction="row">
          <TemplatePlaceholderFillButton
            projectId={projectId}
            docId={docId}
            itemId={item.id}
            templateTypeName={placeholderTemplateType.title}
            templateTypeId={placeholderTemplateType.id}
            templateSearchFilterDefaults={item.templateSearchFilterDefaults}
            templateSearchSortingDefaults={item.templateSearchSortingDefaults}
            templateApplicableFor="QUOTE"
            createItemsWithProducts={itemCreation.createItemsWithProducts}
            createItemsFromTemplates={itemCreation.createItemsFromTemplates}
            onFilled={async () => {
              await onApplied?.({ kind: "templateContent" });
            }}
            label={t("Fill placeholder", { ns: "QuoteItem" })}
            variant="outlined"
            color="secondary"
            refetchWizardQuestions={refetchWizardQuestions}
          />
        </Stack>
      )}
      {originTemplate && (
        <>
          <TemplateListItem
            template={originTemplate}
            selected
            StatusIcons={[<CurrentSelectionChip key="current-selection" />]}
            sx={CURRENT_SELECTION_ROW_SX}
            onInfoClick={() => setInspectingOrigin(true)}
          />
          {inspectingOrigin && (
            <TemplatesQuoteOverviewModal
              docId={originTemplate.id}
              versionNumber={originTemplate.resolvedAsReadModelVersionNumber}
              handleClose={() => setInspectingOrigin(false)}
            />
          )}
        </>
      )}
      <TemplatesDisplay
        allowedViews={ALLOWED_VIEWS}
        collectionViewPersistenceKey="template-placeholder-selection"
        defaultCollectionViewDesktop="list"
        fixedFilters={fixedFilters}
        queryBaseVariables={queryBaseVariables}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        sorting={sorting}
        setSorting={setSorting}
        seedPropertySortings={seedPropertySortings}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        pageSizeOptions={PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onTemplateClick={handleTemplateSelect}
        onInspectedTemplateClick={handleTemplateSelect}
        selectedTemplate={selectedTemplate}
      />
    </Stack>
  );
};
